// Sign-up flow v2 — the six screens, ported from the approved v6 design
// (screens.jsx) and wired to the real API. Everything shown is either the
// owner's own input or a real API response — no sample numbers, no invented
// progress.
import { Fragment, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  APP_STORE_URL, HOURS, LOCS, MODS, money, price, ROLES, SHARES, TOOLS, TOOLS_BY,
  trialEndDate, TYPES, TZS, VENDOR_CHIPS, type ToolKey, type TypeKey,
} from './data';
import type { ImportRowDone, Person, SignupState, VendorRow } from './state';
import { inviteReady, requestSetupCall, vendorFromInvoice, errMsg, type LiveRow } from './wiring';
import { localTeam } from './assistant';

export const Ck = ({ s = 12 }: { s?: number }) => (
  <svg width={s} height={s} viewBox="0 0 12 12" fill="none"><path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

export const GROUPS: Array<[string, number[]]> = [['Account', [0]], ['Your business', [1]], ['Set the plate', [2, 3]], ['Portions', [4]], ['Ready', [5]]];
const cap = (x: string) => (x ? x[0].toUpperCase() + x.slice(1) : x);
export const STEP_NAMES = ['Account', 'Your business', 'Connect', 'Review', 'Portions', 'Ready'];

// ---------- derived state (all real) ----------
export const connected = (s: SignupState): ToolKey[] =>
  (Object.keys(s.tools) as ToolKey[]).filter((k) => s.tools[k] === 'done');
export const hasSource = (s: SignupState) => connected(s).length > 0 || s.alt === 'done';
export const menuSourced = (s: SignupState) => s.itemsImported > 0 || s.menu.some((m) => !m.pushed);
export const itemsCount = (s: SignupState) => s.itemsImported + s.menu.filter((m) => !m.pushed).length;
export const flagged = (s: SignupState) => s.menu.filter((m) => !m.price && !m.pushed).map((m) => m.name);
export const unpriced = (s: SignupState) => flagged(s).filter((n) => !(parseFloat(s.prices[n] || '') > 0));
export const fromName = (s: SignupState): string | null => {
  const c = connected(s);
  if (c.length) return c.map((k) => TOOLS[k].n).join(', ');
  if (s.alt === 'done' || s.menu.length) return s.type === 'cpg' ? 'Your order sheet' : 'Your menu';
  if (s.itemsImported > 0) return 'Your website';
  return null;
};
export const placeOk = (s: SignupState) => !!(s.place.street.trim() && s.place.city.trim() && s.place.phone.trim() && s.place.tz);
export const teamReady = (s: SignupState) => s.team.filter(inviteReady);
export const locCount = (s: SignupState): number => {
  if (s.type === 'cpg') return 1;
  if (s.locationNames.length > 1) return Math.max(1, s.locationNames.filter((_, i) => !s.locOff[i]).length);
  return LOCS[s.locs || '1'] || 1;
};
export const defMods = (s: SignupState): string[] => {
  if (!s.type) return [];
  const m = MODS[s.type];
  const d = [...m.def];
  if (s.alt === 'done' && m.items.some((x) => x[0] === 'pos') && !d.includes('pos')) d.push('pos');
  if (s.type === 'bake' && s.tools.qbo === 'done' && !d.includes('whole')) d.push('whole');
  return d;
};
export const mods = (s: SignupState): string[] => s.mods || defMods(s);
export const salesToolName = (s: SignupState): string | null => s.salesSource;

// ---------- frame parts ----------
export function TopBar({ s, step, onHelp }: { s: SignupState; step: number; onHelp: () => void }) {
  const gi = GROUPS.findIndex((g) => g[1].includes(step));
  return (<>
    <div className="mz-top">
      <img className="logo" src="/mise-wordmark.svg" alt="Mise" />
      <div className="mz-steps">{GROUPS.map(([l], i) => (
        <Fragment key={l}>{i ? <em></em> : null}
          <span className={i === gi ? 'on' : i < gi ? 'dn' : ''}><i>{i < gi ? <Ck s={11} /> : i + 1}</i>{l}</span>
        </Fragment>
      ))}</div>
      {step > 0 && step < 5 ? <span className="saved">Saved</span> : null}
      <span className="mz-cnt">{step < 5 ? 'Step ' + Math.min(gi + 1, 4) + ' of 4' : ''}</span>
      {step < 5 ? <button className="mz-help" onClick={onHelp}>Book a call</button> : null}
    </div>
    <div className="mz-prog"><i style={{ width: ((gi + 1) / 5) * 100 + '%' }}></i></div>
  </>);
}

export function Plate({ s, step, mini }: { s: SignupState; step: number; mini?: boolean }) {
  const m = s.type ? MODS[s.type] : null;
  const items = m ? m.items.slice(0, 6) : Array.from({ length: 6 }, (_, i) => ['x' + i] as unknown as (typeof MODS)['rest']['items'][number]);
  const on = mods(s);
  const n = items.length;
  return (
    <div className={'plate' + (mini ? ' mini' : '')}>
      {items.map((it, i) => {
        const a = ((-90 + (i * 360) / n) * Math.PI) / 180;
        const lit = !!m && step >= 4 && on.includes(it[0]);
        return (
          <b key={it[0]} className={'slot' + (lit ? ' on' : '')} style={{ left: 50 + 31 * Math.cos(a) + '%', top: 50 + 31 * Math.sin(a) + '%', ['--c' as string]: 'var(' + (it[4] || '--p1') + ')' }}>
            {lit && !mini ? it[1].replace(' + kitchen display', '').replace(' & catering', '') : ''}
          </b>
        );
      })}
      {mini ? null : (
        <div className="ctr">{s.type && s.biz
          ? <>{s.biz}<small>{TYPES[s.type].t}</small></>
          : <><img src="/mise-wordmark.svg" alt="Mise" /><small>Your plate</small></>}</div>
      )}
    </div>
  );
}

export function Summary({ s, step }: { s: SignupState; step: number }) {
  const pr = s.type ? price(s.type, mods(s), locCount(s)) : null;
  return (
    <div className="sum">
      <Plate s={s} step={step} mini />
      <span className="sum-t">
        <b>{s.biz && s.type ? s.biz : 'Your plate'}</b>
        <span>{[s.type ? TYPES[s.type].t : null, fromName(s), step >= 4 && pr ? money(pr.total) + '/mo after trial' : null].filter(Boolean).join(' · ') || 'Fills in as you go'}</span>
      </span>
    </div>
  );
}

// ---------- shared step prop bag ----------
export interface StepProps {
  s: SignupState;
  up: (p: Partial<SignupState>) => void;
  get: () => SignupState;
  ask: (text: string, prefill?: boolean) => void;
  back: () => void;
  next: () => void;
  onCall: (reason?: string) => void;
}

// ---------- 1 · Account ----------
export function Account({ s, up, next }: StepProps) {
  const [show, setShow] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [hp, setHp] = useState('');
  // Password managers and browser autofill fill the inputs without React ever
  // hearing about it, so at submit time the DOM — not state — is the source
  // of truth. The button stays enabled and says what's missing out loud; a
  // silently disabled button reads as "broken".
  const submit = () => {
    const read = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
    const name = read('su-n') || s.name;
    const email = read('su-e') || s.email;
    const pw = read('su-p') || s.pw;
    up({ name, email, pw });
    if (!/\S+\s+\S+/.test(name.trim())) { setProblem(name.trim() ? 'Your last name too — it goes on invites and invoices.' : 'Add your name — first and last.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) { setProblem('That email doesn’t look complete — check it once more.'); return; }
    if (pw.length < 8) { setProblem('Pick a password of at least 8 characters.'); return; }
    setProblem(null);
    sessionStorage.setItem('mise-signup-hp', hp);
    next();
  };
  const okPw = s.pw.length >= 8;
  return (
    <div className="mz-in">
      <span className="kick">14 days free · no card</span>
      <h1>Start your free trial</h1>
      <p className="lede">Three short steps and your menu, sales and team are in Mise. We do the typing.</p>
      <form className="form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="fld">
          <label htmlFor="su-n">Your name</label>
          <input id="su-n" className="inp" value={s.name} onChange={(e) => { up({ name: e.target.value }); setProblem(null); }} autoComplete="name" placeholder="First and last" />
        </div>
        <div className="fld">
          <label htmlFor="su-e">Work email</label>
          <input id="su-e" className="inp" type="email" value={s.email} onChange={(e) => { up({ email: e.target.value }); setProblem(null); }} autoComplete="email" placeholder="you@yourplace.com" />
          <p className="hint">We use the domain to find your business, so you don’t have to type it.</p>
        </div>
        <div className="fld">
          <label htmlFor="su-p">Password</label>
          <div className="inp-w">
            <input id="su-p" className="inp" type={show ? 'text' : 'password'} value={s.pw} onChange={(e) => { up({ pw: e.target.value }); setProblem(null); }} autoComplete="new-password" />
            <button type="button" className="eye" onClick={() => setShow(!show)}>{show ? 'Hide' : 'Show'}</button>
          </div>
          <p className={'hint' + (okPw ? ' ok' : '')}>{okPw ? 'Good to go' : 'At least 8 characters'}</p>
        </div>
        {/* Honeypot — invisible to humans, irresistible to bots (same defense as before). */}
        <input type="text" name="website_url" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)}
          style={{ position: 'absolute', left: '-5000px', height: 0, width: 0, opacity: 0 }} aria-hidden="true" />
        {problem ? <p className="hint bad" role="alert" style={{ marginTop: 0 }}>{problem}</p> : null}
        <div className="acts" style={{ marginTop: 8 }}>
          <button className="btn btn-ink">Continue</button>
          <span className="mut">Already use Mise? <Link to="/login">Sign in</Link></span>
        </div>
      </form>
      <p className="fine">By continuing you agree to the <a href="https://lamise.us/terms" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="https://lamise.us/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. Your trial ends {trialEndDate()}. You choose a plan then, or it simply stops.</p>
    </div>
  );
}

// ---------- 2 · Your business ----------
interface BusinessProps extends StepProps {
  found: string | null;
  busy: boolean;
  err: string | null;
  onCreate: () => void;
}

export function Business({ s, up, back, onCall, found, busy, err, onCreate }: BusinessProps) {
  const t = s.type ? TYPES[s.type] : null;
  const ok = !!(s.type && s.biz.trim() && (s.type === 'cpg' || s.locs));
  return (
    <div className="mz-in">
      <span className="kick">Your business</span>
      <h1>What do you run?</h1>
      <p className="lede">This decides what you see first. You can turn any part of Mise on or off later.</p>
      <div className="types">{(Object.entries(TYPES) as Array<[TypeKey, (typeof TYPES)['rest']]>).map(([k, v]) => (
        <button key={k} className={'tt' + (s.type === k ? ' on' : '')} style={{ ['--c' as string]: 'var(' + v.c + ')', ...(s.registered && s.type !== k ? { opacity: 0.45, cursor: 'default' } : {}) }}
          onClick={() => { if (!s.registered) up({ type: k, mods: null, tools: {}, alt: null }); }}>
          <i className="dot"></i><span><b>{v.t}</b><span>{v.ex}</span></span>
        </button>
      ))}</div>
      {s.registered ? <p className="hint" style={{ marginTop: 10 }}>Your account is created as {t ? t.t.toLowerCase() : 'this type'} — you can reshape it any time in Settings.</p> : null}
      {s.type && t ? (
        <div className="form">
          <div className="fld">
            <label htmlFor="su-b">{s.type === 'cpg' ? 'Brand name' : 'Business name'}</label>
            <input id="su-b" className="inp" value={s.biz} onChange={(e) => up({ biz: e.target.value })} />
            {found ? <p className="hint">Found from {found}. Change it if it’s not right.</p> : null}
          </div>
          {s.type !== 'cpg' ? (
            <div className="fld">
              <span className="lbl">How many {t.units}?</span>
              <div className="segs">{['1', '2–4', '5+'].map((x) => (
                <button key={x} className={s.locs === x ? 'on' : ''} onClick={() => up({ locs: x, locOff: {} })}>{x === '5+' ? '5 or more' : x}</button>
              ))}</div>
            </div>
          ) : null}
          <div className="fld">
            <label htmlFor="su-w">Website <span className="opt">optional</span></label>
            <input id="su-w" className="inp" value={s.site} onChange={(e) => up({ site: e.target.value })} placeholder="yourplace.com" />
            <p className="hint">Mise reads it for your address, phone{s.type === 'cpg' ? '' : ', hours'} and {s.type === 'cpg' ? 'products' : 'menu'}.</p>
          </div>
          {s.locs === '5+' && s.type !== 'cpg' ? (
            <div className="note">
              <span><b>With 5 or more, we set it up with you</b>Keep going now; on a 15-min call we pick a pilot store together.</span>
              <button className="btn btn-line btn-sm" onClick={() => onCall('5plus')}>{s.call ? 'Requested · ' + s.call : 'Book the call'}</button>
            </div>
          ) : null}
        </div>
      ) : null}
      {err ? <p className="hint bad" style={{ marginTop: 16 }}>{err}</p> : null}
      <div className="acts">
        <button className="btn btn-ink" disabled={!ok || busy} onClick={onCreate}>
          {busy ? 'Setting up…' : s.registered ? 'Save and continue' : 'Create my account'}
        </button>
        {s.registered ? null : <button className="btn-text" onClick={back}>Back</button>}
      </div>
    </div>
  );
}

// ---------- import checklists (real progress) ----------
export function ImpList({ title, rows }: { title: string; rows: LiveRow[] }) {
  return (
    <div className="imp">
      <h4>{title}</h4>
      {rows.map((r) => (
        <div key={r.label} className={'imp-r' + (r.state === 'wait' ? ' wait' : '')}>
          <i className={'ck' + (r.state === 'dn' ? ' dn' : r.state === 'ld' ? ' ld' : '')}>{r.state === 'dn' ? <Ck s={11} /> : null}</i>
          {r.label}<span className="st">{r.state === 'dn' ? r.detail || '' : ''}</span>
        </div>
      ))}
    </div>
  );
}

const doneRowsToLive = (rows: ImportRowDone[]): LiveRow[] => rows.map((r) => ({ ...r, state: 'dn' as const }));

// ---------- 3 · Connect ----------
interface ConnectProps extends StepProps {
  skip: () => void;
  setSheet: (t: ToolKey) => void;
  liveRows: Partial<Record<ToolKey, LiveRow[]>>;
  onAltFile: (file: File) => void;
  altErr: string | null;
}

export function Connect({ s, up, next, back, skip, setSheet, ask, liveRows, onAltFile, altErr }: ConnectProps) {
  const cpg = s.type === 'cpg';
  const list = s.type ? TOOLS_BY[s.type] : [];
  const fileRef = useRef<HTMLInputElement>(null);
  const click = (t: ToolKey) => { if (!s.tools[t]) setSheet(t); };
  const busy = Object.values(s.tools).includes('importing') || s.alt === 'reading';
  const ok = (hasSource(s) || s.tools.toast === 'call') && !busy;
  return (
    <div className="mz-in">
      <span className="kick">Set the plate</span>
      <h1>{cpg ? 'Where do your orders come from?' : s.type === 'bake' ? 'What do you sell through?' : 'Which POS do you use?'}</h1>
      <p className="lede">{cpg ? 'Connect as many as you like. Mise reads your recent orders and keeps them in sync.' : 'One sign-in brings in your menu and sales. Read-only, nothing overwritten.'}</p>
      <div className="tools">{list.map((t) => {
        const st = s.tools[t]; const T = TOOLS[t];
        return (
          <button key={t} className={'tool' + (st === 'done' || st === 'call' ? ' on' : st === 'importing' ? ' ld' : '')} onClick={() => click(t)}>
            <span className="tag">{st === 'done' ? <><Ck s={10} />Connected</> : st === 'importing' ? 'Connecting…' : st === 'call' ? 'On the call' : T.creds ? 'API keys' : 'Connect'}</span>
            <b>{T.n}</b><span>{T.b}</span>
          </button>
        );
      })}</div>
      <button className={'altb' + (s.alt ? ' on' : '')} onClick={() => !s.alt && up({ alt: 'open' })}>
        <span>
          <b>{cpg ? 'A spreadsheet or a product list' : 'No POS, or a different one'}</b>
          <span>{cpg ? 'Upload your product or order sheet — Mise reads the items and prices' : 'Start from a photo, PDF or CSV of your menu. Your iPhone can be the POS.'}</span>
        </span>
        <span className="go">{s.alt === 'done' ? <Ck s={16} /> : '→'}</span>
      </button>
      {s.alt === 'open' ? (
        <div className="drop">
          <b>{cpg ? 'Drop your product sheet' : 'Drop a photo or PDF of your menu'}</b>
          <p>{cpg ? 'CSV, Excel export or a photo. Mise reads products and prices, and flags anything it can’t read.' : 'Mise reads every item and price, and flags anything it can’t read. You can also paste the menu into the chat.'}</p>
          <div className="acts">
            <button className="btn btn-ink btn-sm" onClick={() => fileRef.current?.click()}>Choose a file</button>
            <button className="btn btn-line btn-sm" onClick={() => ask(cpg ? 'Here are my products:\n' : 'Here is my menu:\n', true)}>Paste it in the chat</button>
          </div>
          <input ref={fileRef} className="filehide" type="file" accept="image/*,.pdf,.csv" onChange={(e) => { const f = e.target.files?.[0]; if (f) onAltFile(f); e.target.value = ''; }} />
          {altErr ? <p className="hint bad">{altErr}</p> : null}
        </div>
      ) : null}
      {s.alt === 'reading' || s.alt === 'done' ? (
        <div className="imp">
          <h4>{s.alt === 'done' ? 'Read from your ' + (s.menu.length && !s.altDetail?.includes('file') ? 'message' : 'file') : 'Reading…'}</h4>
          <div className="imp-r">
            <i className={'ck ' + (s.alt === 'done' ? 'dn' : 'ld')}>{s.alt === 'done' ? <Ck s={11} /> : null}</i>
            {cpg ? 'Products and prices' : 'Items and prices'}
            <span className="st">{s.alt === 'done' ? s.altDetail || '' : ''}</span>
          </div>
        </div>
      ) : null}
      {s.tools.toast === 'call' ? (
        <div className="note" style={{ marginTop: 20 }}>
          <span><b>Toast on the call{s.call ? ' · ' + s.call : ''}</b>We create the API keys with you. Add a menu photo meanwhile if you want to look around.</span>
        </div>
      ) : null}
      {list.map((t) => {
        if (s.tools[t] === 'importing' && liveRows[t]) return <ImpList key={t} title={'Bringing in from ' + TOOLS[t].n + '…'} rows={liveRows[t]!} />;
        if (s.tools[t] === 'done' && s.imports[t]) return <ImpList key={t} title={'In from ' + TOOLS[t].n} rows={doneRowsToLive(s.imports[t]!)} />;
        return null;
      })}
      <div className="acts">
        <button className="btn btn-ink" disabled={!ok} onClick={hasSource(s) ? next : skip}>Continue</button>
        <button className="btn-text" onClick={back}>Back</button>
        <button className="btn-text" style={{ marginLeft: 'auto' }} onClick={skip}>Skip, I’ll connect later</button>
      </div>
    </div>
  );
}

// ---------- the connect sheets ----------
interface OAuthSheetProps {
  s: SignupState;
  t: ToolKey;
  close: () => void;
  onCall: (reason: string) => void;
  ask: (text: string, prefill?: boolean) => void;
  onOAuth: (t: ToolKey, shop?: string) => Promise<void>;
  onToast: (creds: { clientId: string; clientSecret: string; locationGuid: string }) => Promise<void>;
  onFaireToken: (token: string) => Promise<void>;
}

export function OAuthSheet({ s, t, close, onCall, ask, onOAuth, onToast, onFaireToken }: OAuthSheetProps) {
  const T = TOOLS[t];
  const [c, setC] = useState({ id: '', secret: '', guid: '' });
  const [help, setHelp] = useState(false);
  const [shop, setShop] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); } catch (e: any) { setErr(errMsg(e, 'That didn’t go through. Try again in a moment.')); setBusy(false); }
  };

  if (T.creds) {
    const ok = c.id.trim().length >= 10 && c.secret.trim().length >= 10 && /^[0-9a-f-]{10,}$/i.test(c.guid.trim());
    return (
      <div className="scrim" onClick={(e) => e.target === e.currentTarget && close()}>
        <div className="sheet">
          <div className="oauth-from">Mise<i></i>Toast</div>
          <h2>Connect Toast with API keys</h2>
          <p className="sub">Toast lets you issue read access yourself. It takes about 3 minutes, and Mise only reads.</p>
          <button className="btn-text" style={{ minHeight: 32, marginTop: 6 }} onClick={() => setHelp(!help)}>{help ? 'Hide the steps' : 'Where do I find these?'}</button>
          {help ? (
            <ol className="steps">
              <li>In Toast Web, open <b>Integrations › Toast API access</b>.</li>
              <li>Choose <b>Manage credentials</b>, then <b>Create credentials</b>.</li>
              <li>Give it read access to orders, menus and restaurant info.</li>
              <li>Copy the client ID, the secret and your restaurant GUID here.</li>
            </ol>
          ) : null}
          <div className="form" style={{ marginTop: 18, gap: 12 }}>
            <div className="fld"><label htmlFor="tc-i">Client ID</label><input id="tc-i" className="inp" value={c.id} onChange={(e) => setC({ ...c, id: e.target.value })} autoComplete="off" /></div>
            <div className="fld"><label htmlFor="tc-s">Client secret</label><input id="tc-s" className="inp" type="password" value={c.secret} onChange={(e) => setC({ ...c, secret: e.target.value })} autoComplete="off" /></div>
            <div className="fld"><label htmlFor="tc-g">Restaurant GUID</label><input id="tc-g" className="inp" value={c.guid} onChange={(e) => setC({ ...c, guid: e.target.value })} placeholder="e.g. 3f2a9c1e-…" autoComplete="off" /></div>
          </div>
          {err ? <p className="hint bad" style={{ marginTop: 10 }}>{err}</p> : null}
          <div className="acts">
            <button className="btn btn-ink" disabled={!ok || busy} onClick={() => run(() => onToast({ clientId: c.id.trim(), clientSecret: c.secret.trim(), locationGuid: c.guid.trim() }))}>{busy ? 'Checking with Toast…' : 'Connect Toast'}</button>
          </div>
          <p className="fine">Stuck? <button className="lnk" onClick={() => { close(); ask('Where do I find my Toast API keys?'); }}>Ask Mise</button> or <button className="lnk" onClick={() => { close(); onCall('toast'); }}>do it with us on a call</button>.</p>
        </div>
      </div>
    );
  }

  if (t === 'shopify') {
    const norm = shop.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    const full = norm.includes('.') ? norm : norm ? norm + '.myshopify.com' : '';
    const ok = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(full);
    return (
      <div className="scrim" onClick={(e) => e.target === e.currentTarget && close()}>
        <div className="sheet">
          <div className="oauth-from">Mise<i></i>Shopify</div>
          <h2>Let Mise read your Shopify store?</h2>
          <p className="sub">Read-only. Mise never changes anything in Shopify. Disconnect any time in Settings.</p>
          <ul>{SHARES.shopify.map((x) => <li key={x}><i className="ck dn"><Ck s={11} /></i>{x}</li>)}</ul>
          <div className="form" style={{ marginTop: 18, gap: 12 }}>
            <div className="fld">
              <label htmlFor="sp-d">Your store</label>
              <input id="sp-d" className="inp" value={shop} onChange={(e) => setShop(e.target.value)} placeholder="yourstore.myshopify.com" autoComplete="off" />
            </div>
          </div>
          {err ? <p className="hint bad" style={{ marginTop: 10 }}>{err}</p> : null}
          <div className="acts">
            <button className="btn btn-ink" disabled={!ok || busy} onClick={() => run(() => onOAuth('shopify', full))}>{busy ? 'Opening Shopify…' : 'Sign in to Shopify'}</button>
            <button className="btn btn-line" onClick={close}>Cancel</button>
          </div>
          <p className="fine">Opens Shopify’s own sign-in, then brings you straight back here. Your progress is saved.</p>
        </div>
      </div>
    );
  }

  if (t === 'faire') {
    return (
      <div className="scrim" onClick={(e) => e.target === e.currentTarget && close()}>
        <div className="sheet">
          <div className="oauth-from">Mise<i></i>Faire</div>
          <h2>Let Mise read your Faire account?</h2>
          <p className="sub">Read-only. Mise never changes anything in Faire. Disconnect any time in Settings.</p>
          <ul>{SHARES.faire.map((x) => <li key={x}><i className="ck dn"><Ck s={11} /></i>{x}</li>)}</ul>
          {err ? <p className="hint bad" style={{ marginTop: 10 }}>{err}</p> : null}
          <div className="acts">
            <button className="btn btn-ink" disabled={busy} onClick={() => run(() => onOAuth('faire'))}>{busy ? 'Opening Faire…' : 'Sign in to Faire'}</button>
            <button className="btn btn-line" onClick={close}>Cancel</button>
          </div>
          <div className="form" style={{ marginTop: 16, gap: 10 }}>
            <div className="fld">
              <label htmlFor="fa-t">Or paste a brand-portal access token</label>
              <input id="fa-t" className="inp" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" />
              <p className="hint">Faire › Brand portal › Integrations. Works even without the approve screen.</p>
            </div>
            <div className="acts" style={{ marginTop: 0 }}>
              <button className="btn btn-line btn-sm" disabled={token.trim().length < 8 || busy} onClick={() => run(() => onFaireToken(token))}>{busy ? 'Checking…' : 'Connect with the token'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="sheet">
        <div className="oauth-from">Mise<i></i>{T.n}</div>
        <h2>Let Mise read your {T.n} account?</h2>
        <p className="sub">Read-only. Mise never changes anything in {T.n}. Disconnect any time in Settings.</p>
        <ul>{SHARES[t].map((x) => <li key={x}><i className="ck dn"><Ck s={11} /></i>{x}</li>)}</ul>
        {err ? <p className="hint bad" style={{ marginTop: 10 }}>{err}</p> : null}
        <div className="acts">
          <button className="btn btn-ink" disabled={busy} onClick={() => run(() => onOAuth(t))}>{busy ? 'Opening ' + T.n + '…' : 'Sign in to ' + T.n}</button>
          <button className="btn btn-line" onClick={close}>Cancel</button>
        </div>
        <p className="fine">Opens {T.n}’s own sign-in, then brings you straight back here. Your progress is saved.</p>
      </div>
    </div>
  );
}

// ---------- 4 · Review ----------
interface ReviewProps extends StepProps {
  busy: boolean;
  err: string | null;
  onSubmit: () => void;
}

export function Review({ s, up, get, back, ask, busy, err, onSubmit }: ReviewProps) {
  const cpg = s.type === 'cpg';
  const t = s.type ? TYPES[s.type] : TYPES.rest;
  const src = fromName(s);
  const P = s.place;
  const fl = flagged(s);
  const setP = (k: keyof typeof P, v: string) => up({ place: { ...P, [k]: v } });
  const setT = (i: number, k: keyof Person, v: string) => up({ team: s.team.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const setV = (i: number, k: keyof VendorRow, v: string) => up({ vendors: s.vendors.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const addV = (name: string, extra?: Partial<VendorRow>) =>
    !s.vendors.some((v) => v.name === name) && up({ vendors: [...s.vendors, { name, email: '', ...extra }] });
  const [paste, setPaste] = useState('');
  const [invBusy, setInvBusy] = useState(false);
  const [invNote, setInvNote] = useState<string | null>(null);
  const invRef = useRef<HTMLInputElement>(null);
  const ready = teamReady(s);
  // "Sort it out" runs the same deterministic parser the assistant uses —
  // instant, local, no model.
  const sortTeam = () => {
    if (!paste.trim()) return;
    const ppl = localTeam(paste);
    up({ team: [...get().team, ...ppl] });
    setPaste('');
  };
  const onInvoice = async (f: File) => {
    setInvBusy(true); setInvNote(null);
    try {
      const name = await vendorFromInvoice(f);
      if (name) { addV(name, { from: 'invoice' }); setInvNote(`Read “${name}” off the invoice.`); }
      else setInvNote('Couldn’t read a supplier name off that one — add it by hand.');
    } catch (e: any) { setInvNote(errMsg(e, 'Couldn’t read that invoice — a clearer photo usually works.')); }
    setInvBusy(false);
  };
  const L = s.locationNames;
  return (
    <div className="mz-in">
      <span className="kick">Set the plate</span>
      <h1>Here’s what came in</h1>
      <p className="lede">Check it over and fill any gaps. You can also paste things into the chat and Mise fills them in.</p>
      <div className="rv">
        {!cpg && L.length > 1 ? (
          <div className="rv-s">
            <h3>{cap(t.units || 'locations')}<small>From Square</small></h3>
            <div>
              {L.map((n, i) => (
                <button key={n + i} className="chk" onClick={() => up({ locOff: { ...s.locOff, [i]: !s.locOff[i] } })}>
                  <i className={'box' + (s.locOff[i] ? '' : ' on')}>{s.locOff[i] ? null : <Ck s={11} />}</i>{n}
                </button>
              ))}
              {s.locs === '5+' ? <p className="hint">On the call we start with one pilot store. The rest stay connected and ready.</p> : null}
            </div>
          </div>
        ) : null}
        <div className="rv-s">
          <h3>{cpg ? 'Your kitchen' : L.length > 1 ? 'First ' + t.unit : 'Your place'}<small>{P.src ? 'From ' + P.src : 'Needed for receipts and schedules'}</small></h3>
          <div className="grid2">
            <div className="fld full"><label htmlFor="pl-s">Street address</label><input id="pl-s" className="inp" value={P.street} onChange={(e) => setP('street', e.target.value)} autoComplete="street-address" /></div>
            <div className="fld"><label htmlFor="pl-c">City</label><input id="pl-c" className="inp" value={P.city} onChange={(e) => setP('city', e.target.value)} /></div>
            <div className="fld g2">
              <span><label htmlFor="pl-st">State</label><input id="pl-st" className="inp" value={P.state} onChange={(e) => setP('state', e.target.value)} /></span>
              <span><label htmlFor="pl-z">ZIP</label><input id="pl-z" className="inp" value={P.zip} onChange={(e) => setP('zip', e.target.value)} inputMode="numeric" /></span>
            </div>
            <div className="fld"><label htmlFor="pl-p">Phone</label><input id="pl-p" className="inp" value={P.phone} onChange={(e) => setP('phone', e.target.value)} inputMode="tel" /></div>
            <div className="fld"><label htmlFor="pl-t">Time zone</label>
              <select id="pl-t" className="inp" value={P.tz} onChange={(e) => setP('tz', e.target.value)}>
                <option value="">Choose…</option>
                {TZS.map((z) => <option key={z} value={z}>{z.replace('_', ' ')}</option>)}
              </select>
            </div>
            {cpg ? null : <div className="fld full"><label htmlFor="pl-h">Opening hours</label><input id="pl-h" className="inp" value={P.hours} onChange={(e) => setP('hours', e.target.value)} placeholder={s.type ? HOURS[s.type] || 'Mon–Fri 7:00–15:00' : 'Mon–Fri 7:00–15:00'} /></div>}
            {placeOk(s) ? null : <p className="hint full">Or paste your address and hours into the chat.</p>}
          </div>
        </div>
        <div className="rv-s">
          <h3>{cpg ? 'Products' : 'Menu'}<small>{src || 'Not added yet'}</small></h3>
          <div>
            {menuSourced(s) ? <span className="big">{itemsCount(s)} {cpg ? 'SKUs' : 'items'}</span> : (
              <>
                <p className="mut">No {cpg ? 'products' : 'menu'} yet. Paste it into the chat, or add it from the dashboard.</p>
                <button className="btn btn-line btn-sm" style={{ marginTop: 10 }} onClick={() => ask(cpg ? 'Here are my products:\n' : 'Here is my menu:\n', true)}>Paste {cpg ? 'products' : 'my menu'}</button>
              </>
            )}
            {s.menu.filter((m) => !m.pushed).length ? (
              <div className="chips">
                {s.menu.filter((m) => !m.pushed).slice(0, 8).map((x) => <span key={x.name} className="chip">{x.name} · {x.price ? money(x.price, true) : 'no price'}</span>)}
                {s.menu.filter((m) => !m.pushed).length > 8 ? <span className="chip">+{s.menu.filter((m) => !m.pushed).length - 8} more</span> : null}
              </div>
            ) : null}
            {fl.length ? (
              <>
                <p className="mut" style={{ marginTop: 8 }}>{unpriced(s).length ? 'These came in without a price. Add one so nothing rings up at $0.' : 'Every item has a price.'}</p>
                <div className="flags">{fl.map((n) => {
                  const v = s.prices[n];
                  const okP = parseFloat(v || '') > 0;
                  return (
                    <div key={n} className={'flag' + (okP ? ' ok' : '')}>
                      <span>{n}<small>{okP ? money(parseFloat(v), true) : 'No price'}</small></span>
                      <input className="inp sm" inputMode="decimal" placeholder="$0.00" value={v || ''} onChange={(e) => up({ prices: { ...get().prices, [n]: e.target.value.replace(/[^0-9.]/g, '') } })} aria-label={'Price for ' + n} />
                    </div>
                  );
                })}</div>
              </>
            ) : null}
          </div>
        </div>
        {s.salesTotalCents != null && s.salesSource ? (
          <div className="rv-s">
            <h3>Sales<small>{s.salesLabel || 'Recent'}</small></h3>
            <div>
              <span className="big">{money(s.salesTotalCents / 100)}</span>
              {s.salesDays.length ? (
                <div className="spark">{(() => { const max = Math.max(...s.salesDays, 1); return s.salesDays.map((v, i) => <i key={i} style={{ height: Math.max(6, Math.round((v / max) * 100)) + '%', animationDelay: i * 0.03 + 's' }}></i>); })()}</div>
              ) : null}
              <p className="hint">{s.salesSource === 'Toast' ? 'Item-level, by day. Mise uses it for pars, prep and staffing.' : 'From ' + s.salesSource + '. It keeps syncing.'}</p>
            </div>
          </div>
        ) : null}
        {!cpg ? (
          <div className="rv-s">
            <h3>Team<small>{ready.length ? ready.length + ' ready to invite' : 'Optional now'}</small></h3>
            <div>
              {s.team.length ? (
                <div className="ppl">{s.team.map((p, i) => (
                  <div className="pp" key={i}>
                    <input className="inp" value={p.first} onChange={(e) => setT(i, 'first', e.target.value)} placeholder="First" aria-label="First name" />
                    <input className="inp" value={p.last} onChange={(e) => setT(i, 'last', e.target.value)} placeholder="Last" aria-label="Last name" />
                    <input className="inp" type="email" value={p.email} onChange={(e) => setT(i, 'email', e.target.value)} placeholder="Email (for the invite)" aria-label="Email" />
                    <input className="inp" value={p.phone} onChange={(e) => setT(i, 'phone', e.target.value)} placeholder="Mobile (optional)" inputMode="tel" aria-label="Mobile" />
                    <select className="inp" value={p.role} onChange={(e) => setT(i, 'role', e.target.value)} aria-label="Role">{ROLES.map((r) => <option key={r}>{r}</option>)}</select>
                    <button className="x" onClick={() => up({ team: s.team.filter((_, j) => j !== i) })} aria-label="Remove">×</button>
                  </div>
                ))}</div>
              ) : null}
              <textarea className="inp ta" value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={'Paste a list in any shape:\nMaria Lopez, server, maria@gmail.com, 917 555 0142\nDev Patel – cook – dev@gmail.com'}></textarea>
              <div className="acts" style={{ marginTop: 10 }}>
                <button className="btn btn-line btn-sm" disabled={!paste.trim()} onClick={sortTeam}>Sort it out</button>
                <button className="btn-text" onClick={() => up({ team: [...s.team, { first: '', last: '', email: '', phone: '', role: 'Server' }] })}>Add a person</button>
              </div>
              {ready.length ? (
                <>
                  <div className="row" style={{ marginTop: 14 }}>
                    <button className={'tg' + (s.invite ? ' on' : '')} onClick={() => up({ invite: !s.invite })} aria-label="Email invites"></button>
                    <span>Email each one an invite to set their own PIN</span>
                  </div>
                  {s.invite ? (
                    <>
                      <p className="sms-l">What they get, by email</p>
                      <p className="sms">You’re invited to join {s.biz || 'the team'} on Mise — a link to set your own PIN, your role and your first-day details.</p>
                    </>
                  ) : null}
                </>
              ) : s.team.length ? <p className="hint">Invites go by email — add an email to anyone you want invited.</p> : null}
            </div>
          </div>
        ) : null}
        <div className="rv-s">
          <h3>Suppliers<small>{s.vendors.length ? s.vendors.length + ' added' : 'So Mise can draft orders'}</small></h3>
          <div>
            <div className="chips" style={{ marginTop: 0 }}>{(s.type ? VENDOR_CHIPS[s.type] : []).map((v) => (
              <button key={v} className={'chip chipb' + (s.vendors.some((x) => x.name === v) ? ' on' : '')} onClick={() => addV(v)}>{s.vendors.some((x) => x.name === v) ? '✓ ' : '+ '}{v}</button>
            ))}</div>
            {s.vendors.length ? (
              <div className="ppl" style={{ marginTop: 12 }}>{s.vendors.map((v, i) => (
                <div className="pp v" key={v.name + i}>
                  <input className="inp" value={v.name} onChange={(e) => setV(i, 'name', e.target.value)} aria-label="Supplier" />
                  <input className="inp" value={v.email} onChange={(e) => setV(i, 'email', e.target.value)} placeholder="Order email (optional)" aria-label="Order email" />
                  <button className="x" onClick={() => up({ vendors: s.vendors.filter((_, j) => j !== i) })} aria-label="Remove">×</button>
                </div>
              ))}</div>
            ) : null}
            <div className="acts" style={{ marginTop: 10 }}>
              <button className="btn btn-line btn-sm" disabled={invBusy} onClick={() => invRef.current?.click()}>{invBusy ? 'Reading…' : 'Snap an invoice'}</button>
              <button className="btn-text" onClick={() => ask('Our suppliers are ', true)}>Tell Mise in the chat</button>
              <input ref={invRef} className="filehide" type="file" accept="image/*,.pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) void onInvoice(f); e.target.value = ''; }} />
            </div>
            {invNote ? <p className="hint">{invNote}</p> : null}
          </div>
        </div>
      </div>
      {err ? <p className="hint bad" style={{ marginTop: 16 }}>{err}</p> : null}
      <div className="acts">
        <button className="btn btn-ink" disabled={!placeOk(s) || busy} onClick={onSubmit}>{busy ? 'Saving…' : 'Looks right'}</button>
        <button className="btn-text" onClick={back}>Back</button>
        {placeOk(s) ? null : <span className="hint" style={{ margin: 0 }}>Add the address, phone and time zone to continue.</span>}
      </div>
    </div>
  );
}

// ---------- 5 · Portions ----------
interface PortionsProps extends StepProps {
  busy: boolean;
  err: string | null;
  onFinish: () => void;
}

export function Portions({ s, up, back, busy, err, onFinish }: PortionsProps) {
  if (!s.type) return null;
  const m = MODS[s.type];
  const on = mods(s);
  const d = defMods(s);
  const L = locCount(s);
  const t = TYPES[s.type];
  const pr = price(s.type, on, L);
  const tog = (id: string) => up({ mods: on.includes(id) ? on.filter((x) => x !== id) : [...on, id] });
  return (
    <div className="mz-in">
      <span className="kick">Pick your portions</span>
      <h1>What should Mise run for you?</h1>
      <p className="lede">Everything is on for your 14 days. This sets what you see first, and what it would cost after.</p>
      <div className="pl">
        <div className="pi lock">
          <i className="dot" style={{ background: 'var(--tint)', boxShadow: 'inset 0 0 0 1.5px var(--ink)' }}></i>
          <span className="nm"><b>{m.core.n}<span className="rec">Always on</span></b><span>{m.core.d}</span></span>
          <span className="pr">{money(m.core.p)}<small>{m.flat ? 'per month' : 'per ' + t.unit}</small></span>
        </div>
        {m.items.map(([id, n, p, dd, c, flat]) => {
          const o = on.includes(id);
          return (
            <button key={id} className={'pi' + (o ? '' : ' off') + (m.flat ? ' lock' : '')} style={{ ['--c' as string]: 'var(' + c + ')' }} onClick={() => !m.flat && tog(id)} aria-pressed={o}>
              <i className="dot"></i>
              <span className="nm"><b>{n}{!m.flat && d.includes(id) ? <span className="rec">Suggested</span> : null}</b><span>{dd}</span></span>
              <span className="pr">{m.flat ? 'Included' : '+' + money(p)}{m.flat ? null : <small>{flat ? 'per month' : 'per ' + t.unit}</small>}</span>
              {m.flat ? null : <i className={'box' + (o ? ' on' : '')}>{o ? <Ck s={11} /> : null}</i>}
            </button>
          );
        })}
      </div>
      <div className="tot">
        <div>
          <span className="mut">After your trial</span>
          <div className="big">{money(pr.total)}<span className="mut" style={{ fontSize: 16, letterSpacing: 0 }}> / month</span></div>
          {pr.per ? <p className="hint">{L} {L === 1 ? t.unit : t.units}{pr.disc ? ' · ' + pr.disc * 100 + '% multi-location discount' : ''}</p> : <p className="hint">The whole company. Never per SKU.</p>}
        </div>
        <p className="mut" style={{ maxWidth: 240 }}>No card today. We email you 3 days before the trial ends.</p>
      </div>
      {err ? <p className="hint bad" style={{ marginTop: 16 }}>{err}</p> : null}
      <div className="acts">
        <button className="btn btn-ink" disabled={busy} onClick={onFinish}>{busy ? 'Finishing…' : 'Finish setup'}</button>
        <button className="btn-text" onClick={back}>Back</button>
      </div>
    </div>
  );
}

// ---------- 6 · Done ----------
interface DoneProps {
  s: SignupState;
  up: (p: Partial<SignupState>) => void;
  ask: (text: string, prefill?: boolean) => void;
  onOpenDashboard: (path?: string) => void;
  onSendInvites: () => void;
}

export function Done({ s, up, ask, onOpenDashboard, onSendInvites }: DoneProps) {
  const cpg = s.type === 'cpg';
  const t = s.type ? TYPES[s.type] : TYPES.rest;
  const L = locCount(s);
  const ready = teamReady(s);
  const items = itemsCount(s);

  const did: Array<[string, string]> = [];
  if (items > 0) did.push([`Brought in ${items} ${cpg ? 'products' : 'menu items'}`, fromName(s) ? 'From ' + fromName(s) : '']);
  if (s.salesSource) did.push([`Synced your sales from ${s.salesSource}`, s.salesSource === 'Toast' ? 'By business day, item-level. Pars and prep start from these.' : 'It keeps syncing on its own']);
  if (placeOk(s)) did.push(['Set your address, phone and time zone', s.place.tz.replace('_', ' ')]);
  if (s.invitedCount > 0) did.push([`Emailed ${s.invitedCount} ${s.invitedCount === 1 ? 'person' : 'people'} an invite`, 'Each one sets their own PIN']);
  if (s.vendorsCreated > 0) did.push([`Added ${s.vendorsCreated} supplier${s.vendorsCreated > 1 ? 's' : ''}`, s.vendors.filter((v) => v.created).map((v) => v.name).join(', ')]);
  if (s.faireCreated != null && s.faireCreated > 0) did.push([`Brought in ${s.faireCreated} Faire order${s.faireCreated === 1 ? '' : 's'}`, 'In Wholesale, under Faire']);

  const need: Array<[string, string, string, string | null, (() => void) | null]> = [];
  if (!menuSourced(s)) need.push(['menu', cpg ? 'Add your products' : 'Add your menu', 'Paste it in the chat or drop a photo', 'Add', () => ask(cpg ? 'Here are my products:\n' : 'Here is my menu:\n', true)]);
  const unp = unpriced(s);
  if (unp.length) need.push(['price', `Price ${unp.length} item${unp.length > 1 ? 's' : ''}`, unp.slice(0, 5).join(', ') + (unp.length > 5 ? '…' : '') + ' — set them on the Menu page', null, null]);
  if (!cpg && !s.team.length) need.push(['team', 'Add your team', 'Paste a list; each person gets an email invite', 'Add', () => ask('Here is my team:\n', true)]);
  if (!cpg && ready.length > s.invitedCount) need.push(['inv', `Invite ${ready.length - s.invitedCount} ${ready.length - s.invitedCount === 1 ? 'person' : 'people'} by email`, 'They set their own PIN and see their shifts', 'Send', onSendInvites]);
  if (!s.vendors.length) need.push(['vend', 'Add your suppliers', 'Snap one invoice from each; Mise reads the items and prices', 'Add', () => ask('Our suppliers are ', true)]);
  if (cpg) need.push(['prod', 'Check your first production plan', 'Built from open orders and what’s on the shelf', 'Open', () => onOpenDashboard('/production')]);
  else if (s.type === 'bake') need.push(['bake', 'Check tomorrow’s bake', s.salesSource ? 'Built from your sales as they sync' : 'Starts once sales come in', 'Open', () => onOpenDashboard('/production')]);
  else need.push(['count', s.type === 'bar' ? 'Walk the back bar once' : 'Count your shelf once', s.vendors.length ? 'With a starting count, Mise drafts your orders' : 'Snap a photo in the app; Mise counts what it sees', 'Show me', () => onOpenDashboard('/counts')]);
  if (s.call) need.unshift(['call', 'Setup call · ' + s.call, s.tools.toast === 'call' ? 'We create the Toast keys with you' : 'We pick a pilot store together', null, null]);

  const stats: Array<[string, string]> = [
    ...(s.salesTotalCents != null ? [[money(s.salesTotalCents / 100), `Sales, ${(s.salesLabel || 'recent').toLowerCase()}`] as [string, string]] : []),
    ...(items > 0 ? [[String(items), cpg ? 'Products' : 'Menu items'] as [string, string]] : []),
    ...(!cpg ? [[String(L), L === 1 ? cap(t.unit) : cap(t.units || 'locations')] as [string, string]] : []),
    ...(s.invitedCount > 0 ? [[String(s.invitedCount), 'Invited'] as [string, string]] : []),
    ...(s.vendorsCreated > 0 ? [[String(s.vendorsCreated), 'Suppliers'] as [string, string]] : []),
  ].slice(0, 4);

  return (
    <div className="dn">
      <div className="trial">
        <span><b>Free until {trialEndDate()}.</b> No card on file. We’ll email you 3 days before with exactly what it would cost.</span>
        <button className="btn btn-line btn-sm" onClick={() => onOpenDashboard('/billing')}>See plans</button>
      </div>
      <span className="kick">{s.biz}</span>
      <h1>You’re set up</h1>
      <p className="lede">Here’s what Mise already did, and the short list that needs you.</p>
      {stats.length ? <div className="stats">{stats.map(([v, l]) => <div className="stat" key={l}><b>{v}</b><span>{l}</span></div>)}</div> : null}
      <div className="cols">
        <div>
          <h2>Mise already did</h2><p>While you were setting up.</p>
          <div className="lst">{did.length ? did.map(([a, b]) => (
            <div className="li" key={a}><i className="ck dn"><Ck s={11} /></i><span className="nm"><b>{a}</b>{b ? <span>{b}</span> : null}</span></div>
          )) : <div className="li"><i className="ck dn"><Ck s={11} /></i><span className="nm"><b>Created your workspace</b><span>Everything is on for the trial</span></span></div>}</div>
        </div>
        <div>
          <h2>Needs you</h2><p>{need.length} thing{need.length === 1 ? '' : 's'}, a few minutes each.</p>
          <div className="lst">{need.map(([id, a, b, cta, act]) => {
            const dn = s.doneTasks[id];
            return (
              <div className="li" key={id}>
                <i className={'ck' + (dn ? ' dn' : '')}>{dn ? <Ck s={11} /> : null}</i>
                <span className="nm"><b>{a}</b><span>{b}</span></span>
                {cta && act && !dn ? <button className="btn btn-line btn-sm" onClick={() => { act(); if (id === 'inv') up({ doneTasks: { ...s.doneTasks, [id]: true } }); }}>{cta}</button>
                  : id === 'call' ? <span className="when">Requested</span>
                  : dn ? <span className="when">Done</span> : null}
              </div>
            );
          })}</div>
        </div>
      </div>
      <div className="appc">
        <span><b>Mise on your phone</b><span>Counts, orders and the assistant. Sign in with {s.email}. Same account, nothing new to set up.</span></span>
        <a className="btn btn-sm" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Open the App Store</a>
      </div>
      <div className="acts"><button className="btn btn-ink" onClick={() => onOpenDashboard()}>Open my dashboard</button></div>
    </div>
  );
}

// ---------- Book a call ----------
interface CallSheetProps {
  s: SignupState;
  up: (p: Partial<SignupState>) => void;
  reason: string;
  close: () => void;
}

/** No fake calendar here: when the account exists this files a real setup-help
 * request (POST /onboarding/request-help) with a preferred window — a person
 * emails back to confirm. Before the account exists it's a plain email. */
export function CallSheet({ s, up, reason, close }: CallSheetProps) {
  const [pick, setPick] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const SL = ['Today, afternoon', 'Tomorrow, morning', 'Tomorrow, afternoon', 'This week, anytime'];
  const send = async () => {
    if (!pick) return;
    setBusy(true); setErr(null);
    try {
      await requestSetupCall(STEP_NAMES[s.step] || 'signup', pick, reason === 'toast' ? 'Toast API keys' : reason === '5plus' ? '5+ locations pilot' : null, s.email);
      up({ call: pick, ...(reason === 'toast' ? { tools: { ...s.tools, toast: 'call' as const } } : {}) });
      close();
    } catch (e: any) { setErr(errMsg(e, 'Couldn’t send that just now — email us instead and we’ll sort it.')); setBusy(false); }
  };
  return (
    <div className="scrim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="sheet">
        <h2>{reason === 'toast' ? 'Connect Toast on a quick call' : 'Book a 15-minute call'}</h2>
        <p className="sub">{reason === 'toast' ? 'We create the Toast API keys with you and connect them, in about 15 minutes.' : 'A person from Mise sets it up with you. Everything you’ve done so far is saved.'}</p>
        {s.registered ? (
          <>
            <div className="slots">{SL.map((x) => <button key={x} className={pick === x ? 'on' : ''} onClick={() => setPick(x)}>{x}</button>)}</div>
            {err ? <p className="hint bad" style={{ marginTop: 10 }}>{err}</p> : null}
            <div className="acts">
              <button className="btn btn-ink" disabled={!pick || busy} onClick={send}>{busy ? 'Sending…' : 'Request ' + (pick || 'a time')}</button>
              <button className="btn btn-line" onClick={close}>Not now</button>
            </div>
            <p className="fine">We’ll reply to {s.email || 'your email'} to confirm the exact time.</p>
          </>
        ) : (
          <>
            <div className="acts">
              <a className="btn btn-ink" href={'mailto:leah@mystep.ai?subject=' + encodeURIComponent('Mise setup call') + '&body=' + encodeURIComponent('I’d like a 15-minute setup call.' + (s.biz ? ` Business: ${s.biz}.` : ''))}>Email us</a>
              <button className="btn btn-line" onClick={close}>Not now</button>
            </div>
            <p className="fine">Once your account exists, this books in one tap. For now, an email reaches a person directly.</p>
          </>
        )}
      </div>
    </div>
  );
}
